import React from 'react'
import { Formik, Form } from 'formik'
import styled from 'styled-components'
import { useMutation } from '@apollo/client/react'
import { Button } from '../../../components/Button'
import { ModalContainer, ModalWindow, ModalWindowContent, ModalHeader, Text } from '../../../components/layout'
import { TextInput, SelectInput } from '../../../components/layout/form/input'
import { setSuccessNotification, setErrorNotification } from '../../../components/notification'
import emailValidator from 'email-validator'

import {
  SEND_TOWING_DOCUMENT_LINK,
  SendTowingDocumentLinkMutation,
  SendTowingDocumentLinkMutationVariables,
} from './queries'
import { NotificationMethod, notificationMethods } from '../../../common/types'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { TowingRecord } from './types'

interface Props {
  towingRecordId: number
  options: ContactOption[]
  onClose: () => void
}

interface ContactOption {
  id: string
  label: string
  type: NotificationMethod
  value: string
}

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    flex-direction: column;
  }
`

const MethodButton = styled.button<{ isSelected: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: ${props => props.theme.radius.large};
  background-color: ${props => (props.isSelected ? '#1565c0' : props.theme.colors.grey300)};
  color: ${props => (props.isSelected ? 'white' : props.theme.colors.black)};
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: ${props => (props.isSelected ? '#0d47a1' : props.theme.colors.grey500)};
  }

  &:active {
    background-color: #0d47a1;
    color: white;
  }

  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    min-width: auto;
    width: 100%;
  }
`

const SendButton = styled(Button)`
  && {
    width: 100% !important;
    background-color: #2e7d32;
    border-color: #2e7d32;

    &:hover:not(:disabled) {
      background-color: #1b5e20;
      border-color: #1b5e20;
    }

    &:disabled {
      background-color: #999999;
      border-color: #999999;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`

const CancelButton = styled(Button)`
  && {
    width: 100% !important;
    background-color: #c62828;
    border-color: #c62828;
    color: ${props => props.theme.colors.staticWhite};
    &:hover:not(:disabled) {
      background-color: #b71c1c;
      border-color: #b71c1c;
    }
    &:disabled {
      background-color: #999999;
      border-color: #999999;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;

  > div {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    max-width: 300px;
    margin: 0 auto;

    > div {
      min-width: auto;
      width: 100%;
    }
  }
`
interface InitialValues {
  contact: string
  sendBy: "email" | "sms"
  email: string
  phone: string
  shouldValidate: boolean
}

const validateForm = (values: InitialValues) => {
  const errors: Partial<Record<keyof InitialValues, string>> = {}

  // Skip validation if values came from options
  if (!values.shouldValidate) {
    return errors
  }

  if (values.sendBy === 'email') {
    const email = values.email || ''
    if (!email || email.trim() === '') {
      errors.email = 'Sähköpostiosoite vaaditaan'
    } else if (!emailValidator.validate(email)) {
      errors.email = 'Virheellinen sähköpostiosoite'
    }
  }

  if (values.sendBy === 'sms') {
    const phone = values.phone || ''
    if (!phone || phone.trim() === '') {
      errors.phone = 'Puhelinnumero vaaditaan'
    } else {
      const phoneNumber = parsePhoneNumberFromString(phone, 'FI')
      if (!phoneNumber || !phoneNumber.isValid()) {
        errors.phone = 'Virheellinen puhelinnumero'
      }
    }
  }

  return errors
}



export const SendDocumentForm: React.FC<Props> = ({ towingRecordId, options, onClose }) => {
  const [sendDocument, { loading }] = useMutation<
    SendTowingDocumentLinkMutation,
    SendTowingDocumentLinkMutationVariables
  >(SEND_TOWING_DOCUMENT_LINK, {
    onCompleted(data) {
      if (data.sendTowingDocumentLink.__typename === 'SendTowingDocumentLinkSuccess') {
        setSuccessNotification(
          'Lähetys onnistui',
          data.sendTowingDocumentLink.message || `Kuormakirjan linkki lähetetty onnistuneesti`
        )
        onClose()
      } else {
        setErrorNotification('Virhe', 'Kuormakirjan lähettäminen epäonnistui')
      }
    },
    onError(error) {
      setErrorNotification('Virhe', error?.message || 'Virhe kuormakirjaa lähettäessä')
    },
  })

  const initialValues: InitialValues = {
    contact: '',
    sendBy: notificationMethods.email,
    email: '',
    phone: '',
    shouldValidate: false,
  }

  return (
    <ModalContainer backgroundColor="rgba(0, 0, 0, 0.6)">
      <ModalWindow>
        <ModalHeader>Lähetä kuormakirja</ModalHeader>
        <ModalWindowContent>
          <Formik
            initialValues={initialValues}
            validate={validateForm}
            validateOnMount={true}
            validateOnChange={true}
            onSubmit={async values => {
              await sendDocument({
                variables: {
                  input: {
                    towingRecordId,
                    sendBy: values.sendBy,
                    email: values.email,
                    phone: values.phone,
                  },
                },
              })
            }}
          >
            {({ values, setFieldValue }) => (
              <Form>
                {options.length > 0 && (
                  <SelectInput
                    name="contact"
                    label="Tuo kuormakirjan tiedoista"
                    unsetAfterSelect
                    options={options.map(opt => ({ label: opt.label, value: opt.id }))}
                    placeholder="Valitse"
                    onChange={(value: string | number | undefined) => {
                      const selectedOption = options.find(opt => opt.id === String(value))
                      if (selectedOption && value) {
                        setFieldValue('sendBy', selectedOption.type)
                        if (selectedOption.type === notificationMethods.email) {
                          setFieldValue('email', selectedOption.value)
                        } else if (selectedOption.type === notificationMethods.sms) {
                          setFieldValue('phone', selectedOption.value)
                        }
                      
                      }
                      setFieldValue('shouldValidate', false) 
                    }}
                  />
                )}

                <FormSection style={{ marginTop: '1.5rem' }}>
                  <Text >
                    Lähetystapa
                  </Text>
                  <ButtonGroup>
                    <MethodButton
                      type="button"
                      isSelected={values.sendBy === notificationMethods.email}
                      onClick={() => {
                        setFieldValue('sendBy', notificationMethods.email)
                        setFieldValue('phone', '')
                        setFieldValue('shouldValidate', true)
                      }}
                    >
                      Sähköposti
                    </MethodButton>
                    <MethodButton
                      type="button"
                      isSelected={values.sendBy === notificationMethods.sms}
                      onClick={() => {
                        setFieldValue('sendBy', notificationMethods.sms)
                        setFieldValue('email', '')
                        setFieldValue('shouldValidate', true)
                      }}
                    >
                      Tekstiviesti
                    </MethodButton>
                  </ButtonGroup>
                </FormSection>
                
                {values.sendBy === notificationMethods.email && (
                  <FormSection>
                    <TextInput
                      name="email"
                      label="Sähköpostiosoite"
                      type="email"
                      onChange={() => setFieldValue('shouldValidate', true)}
                    />
                  </FormSection>
                )}

                {values.sendBy === notificationMethods.sms && (
                  <FormSection>
                    <TextInput
                      name="phone"
                      label="Puhelinnumero"
                      type="tel"
                      onChange={() => setFieldValue('shouldValidate', true)}
                    />
                  </FormSection>
                )}

                <ButtonContainer>
                  <div>
                    <SendButton
                      submit
                      label="Lähetä"
                      category="on"
                      size="m"
                      fontWeight="normal"
                      loading={loading}
                      disabled={
                        loading ||
                      
                        (values.sendBy === notificationMethods.email && !values.email?.trim()) ||
                        (values.sendBy === notificationMethods.sms && !values.phone?.trim())
                      }
                    />
                  </div>
                  <div>
                    <CancelButton label="Peruuta" category="off" size="m" fontWeight="normal" onClick={onClose} />
                  </div>
                </ButtonContainer>
              </Form>
            )}
          </Formik>
        </ModalWindowContent>
      </ModalWindow>
    </ModalContainer>
  )
}

export function toSendDocumentOptions(towingRecord: TowingRecord): ContactOption[] {
  const options: ContactOption[] = []

  const orderer = towingRecord?.orderer
  const signature = towingRecord?.signature

  if (orderer?.email) {
    options.push({
      id: 'orderer_email',
      label: `Tilaajan sähköposti: ${orderer.email}`,
      type: notificationMethods.email,
      value: orderer.email,
    })
  }

  if (orderer?.phone) {
    options.push({
      id: 'orderer_phone',
      label: `Tilaajan puhelinnumero: ${orderer.phone}`,
      type: notificationMethods.sms,
      value: orderer.phone,
    })
  }

  if (signature?.signatorEmail) {
    options.push({
      id: 'signer_email',
      label: `Allekirjoittajan sähköposti: ${signature.signatorEmail}`,
      type: notificationMethods.email,
      value: signature.signatorEmail,
    })
  }

  if (signature?.signatorPhone) {
    options.push({
      id: 'signer_phone',
      label: `Allekirjoittajan puhelinnumero: ${signature.signatorPhone}`,
      type: notificationMethods.sms,
      value: signature.signatorPhone,
    })
  }
  return options
}
